// ポテンシャル分析設定 型定義（FKV3-13111）
// バックエンドAPI /{accountCode}/potential-analysis-settings の契約に対応する

/**
 * 設定できる集計階層の上限。バックエンド・DB の CHECK 制約と合わせる。
 * 名称は Athena の `product_groups.product_group_level` に揃えている。
 *
 * **最下層まで出すときもこの値を使う。** 商品グループの階層は最大5で、
 * 5 に満たないタイプに 5 を指定するとそのタイプの最下層までのフルパスになる
 */
export const MAX_PRODUCT_GROUP_LEVEL = 5;

/** 階層を指定しないときの既定（縦持ち）。最下層まで出す */
export const DEFAULT_PRODUCT_GROUP_LEVEL = MAX_PRODUCT_GROUP_LEVEL;

/** 持てる掛け合わせの組数の上限。画面は当面1組しか作らせない */
export const MAX_CROSS_SETS = 3;

/** 画面が扱う掛け合わせの組数。複数組を開くときはここを増やす */
export const EDITABLE_CROSS_SETS = 1;

/** 非稼働判定日数の範囲。バックエンド・DB の CHECK 制約と合わせる */
export const MIN_INACTIVE_DAYS = 1;
export const MAX_INACTIVE_DAYS = 365;

/**
 * 非稼働判定日数の既定値。CommonDB の DEFAULT（`inactive_stock_day_threshold`）と合わせる。
 * 同じ「遡って販売が無い日数」を見る app_stock_settings.worst_stock_lookup_sales_day に揃えた値
 */
export const DEFAULT_INACTIVE_DAYS = 365;

/** 掛け合わせで指定できる軸の数（FKV3-13122）。集計側のSQLが3スロット固定 */
export const MAX_CROSS_AXES = 3;

/**
 * 掛け合わせで軸を選んだときの既定の集計階層。
 *
 * 縦持ちは最下層が既定だが、掛け合わせは行数が各軸のグループ数の積で増えるため1階層から始める。
 * 都度実行の抽出でも同じ既定にしており、abdi の実測では最下層→1階層で結果が20分の1になった。
 */
export const CROSS_DEFAULT_PRODUCT_GROUP_LEVEL = 1;

/**
 * 商品グループタイプ1件の設定（マスタの一覧に設定を重ねたもの）。
 *
 * 出力対象かどうかは設定テーブルの行の存在で決まるため、未設定のタイプは
 * `enabled = false`・`product_group_level = null` で返る。
 */
export interface PotentialAnalysisProductGroupTypeSetting {
  product_group_type_id: string;
  /** 商品グループタイプ名。マスタから消えたタイプは null */
  name: string | null;
  /** マスタの並び順。マスタから消えたタイプは null */
  sort_order: number | null;
  /** 出力対象かどうか */
  enabled: boolean;
  /** 出力する階層（1〜5）。最下層まで出すときは 5 */
  product_group_level: number;
  /** 設定はあるがマスタに無いタイプ。集計ジョブは読み飛ばすため、画面が削除を促す */
  missing_from_master: boolean;
}

/** 掛け合わせの軸1件（FKV3-13122）。配列の位置が出力の列順になる */
export interface PotentialAnalysisCrossAxisSetting {
  product_group_type_id: string;
  /** 商品グループタイプ名。マスタから消えたタイプは null */
  name: string | null;
  /** 出力する階層（1〜5）。最下層まで出すときは 5 */
  product_group_level: number;
  /** マスタに無いタイプ。集計ジョブは読み飛ばすため、画面が付け替えを促す */
  missing_from_master: boolean;
}

/** 掛け合わせの1組 */
export interface PotentialAnalysisCrossSetSetting {
  /** 組み合わせID。保存時に引き継ぐ */
  id: string;
  /** 軸（1〜3）。前から詰まっている */
  axes: PotentialAnalysisCrossAxisSetting[];
}

/** ポテンシャル分析設定の取得レスポンス */
export interface PotentialAnalysisSetting {
  product_group_types: PotentialAnalysisProductGroupTypeSetting[];
  /** 掛け合わせの組。未設定なら空配列 */
  cross_sets: PotentialAnalysisCrossSetSetting[];
  /**
   * 商品グループタイプのマスタ（Athena）を引けたか。
   *
   * false のとき `product_group_types` は設定済みのタイプだけで、タイプ名も入らない。
   * 保存直後の応答でだけ起こる（取得時はマスタを引けなければエラーになる）。
   * 画面はこのとき一覧を差し替えず、選択状態だけを反映する。
   */
  master_available: boolean;
  /** 非稼働判定日数（1〜365）。未設定なら既定の30 */
  inactive_days: number;
}

/** 更新リクエストの商品グループタイプ1件 */
export interface PotentialAnalysisProductGroupTypeSelection {
  product_group_type_id: string;
  /** 出力する階層（1〜5）。最下層まで出すときは 5 */
  product_group_level: number;
}

/** 更新リクエストの掛け合わせ軸1件 */
export interface PotentialAnalysisCrossAxisSelection {
  product_group_type_id: string;
  /** 出力する階層（1〜5）。最下層まで出すときは 5 */
  product_group_level: number;
}

/** 更新リクエストの掛け合わせ1組 */
export interface PotentialAnalysisCrossSetSelection {
  id: string;
  /** 軸（1〜3）。配列の順序が出力の列順になるため、空きを詰めて送る */
  axes: PotentialAnalysisCrossAxisSelection[];
}

/** ポテンシャル分析設定の更新リクエスト（差分ではなく全置換） */
export interface UpdatePotentialAnalysisSettingRequest {
  product_group_types: PotentialAnalysisProductGroupTypeSelection[];
  /** 掛け合わせの組（最大 MAX_CROSS_SETS）。空なら掛け合わせを出力しない */
  cross_sets: PotentialAnalysisCrossSetSelection[];
  inactive_days: number;
}
